"use client";

import React from "react";

import { useTranslations } from "next-intl";

import { cn } from "@/utils/cn";

export interface PropertyAssignmentProps {
  /** CSS class name for custom styling */
  className?: string;
  /** Property ID */
  propertyId: string;
  /** Currently assigned realtor */
  assignedRealtor?: {
    id: string;
    name: string;
    avatar?: string;
  };
  /** Current user */
  currentUser?: {
    id: string;
    name: string;
  };
  /** Callback when user wants to assign property to themselves */
  onAssignToMe?: () => void;
  /** Whether the current user can assign properties */
  canAssign?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Property status */
  status?: "pending" | "active" | "inactive" | "sold" | "archived";
}

/**
 * PropertyAssignment - Shows property status and assignment according to Figma design
 *
 * Figma Design Specifications (6001-3):
 * - Container: 645.58×50px
 * - "Статус:": left, font-size: 15px, font-weight: 500, color: #373737
 * - Status badge: 155×50px, white bg, #ECECEC border, border-radius: 15px, shadow
 * - "Закреплен за: Ната Саркисян": center, font-size: 15px, font-weight: 500, color: #373737
 * - "Назначить мне": right, font-size: 15px, font-weight: 500, color: #373737
 */
export const PropertyAssignment: React.FC<PropertyAssignmentProps> = ({
  className,
  propertyId,
  assignedRealtor,
  currentUser,
  onAssignToMe,
  canAssign = true,
  loading = false,
  status = "pending",
}) => {
  const t = useTranslations("realtor");
  const isAssignedToCurrentUser = assignedRealtor?.id === currentUser?.id;

  // Status text mapping using realtor translations
  const getStatusText = (status: string) => {
    const statusMap = {
      pending: t("statusPending"),
      active: t("statusActive"),
      inactive: t("statusInactive"),
      sold: t("statusSold"),
      archived: t("statusArchived"),
      unknown: t("statusUnknown"),
      exclusive: t("statusExclusive"),
      ready: t("statusReady"),
      under_construction: t("statusUnderConstruction"),
      in_progress: t("statusInProgress"),
      planned: t("statusPlanned"),
    };
    return statusMap[status as keyof typeof statusMap] || t("statusPending");
  };

  return (
    <div
      className={cn(
        "flex items-center justify-between",
        "w-full h-[50px]", // Exact Figma height
        className,
      )}
      data-testid="property-assignment"
    >
      {/* Left: Status label */}
      <span className="text-[15px] font-medium text-[#373737] leading-[19px]">
        {t("propertyAssignmentStatusLabel")}
      </span>

      {/* Center: Status Badge - 155×50px */}
      <div
        className={cn(
          "inline-flex items-center justify-center",
          " h-[50px] px-4", // Exact Figma dimensions
          "bg-white border border-[#ECECEC]", // Exact colors
          "rounded-[15px]", // Exact border-radius
          "shadow-[5px_5px_25px_15px_rgba(235,235,235,0.25)]", // Exact shadow
        )}
      >
        <span className="text-[16px] font-medium text-[#444444] leading-[20px]">
          {getStatusText(status)}
        </span>
      </div>

      {/* Center-Right: Assignment info */}
      <div className="flex items-center text-[15px] font-medium text-[#373737] leading-[19px]">
        {assignedRealtor ? (
          <span>
            {t("propertyAssignmentAssignedTo", { name: assignedRealtor.name })}
          </span>
        ) : (
          <span>{t("propertyAssignmentUnassigned")}</span>
        )}
      </div>

      {/* Right: "Назначить мне" button */}
      {canAssign && !isAssignedToCurrentUser && (
        <button
          type="button"
          className={cn(
            "text-[15px] font-medium text-[#373737] leading-[19px]",
            "hover:text-[#CF1409] hover:underline",
            "transition-colors duration-200",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
          onClick={() => {
            onAssignToMe?.();
          }}
          disabled={loading}
          data-testid="assign-to-me-button"
        >
          {loading
            ? t("propertyAssignmentAssignToMeLoading")
            : t("propertyAssignmentAssignToMe")}
        </button>
      )}
    </div>
  );
};

export default PropertyAssignment;
